/**
 * When to pull (spec §6).
 *
 * Four triggers, and deliberately no timer: on a phone in the field, battery
 * and bandwidth matter more than freshness. `pullAll()` de-dupes overlapping
 * callers, so triggers firing together cost one pull.
 *
 *   1. Foreground after more than 60s backgrounded.
 *   2. Explicit pull-to-refresh.            → `refreshNow()`
 *   3. After a successful drain.            → `syncNow()`
 *   4. Manual Refresh on the Sync screen.   → `refreshNow()`
 *   5. The radio coming back.               → `online`
 *   6. New local work.                      → `enqueue()` schedules a push
 *   7. App start, if online.                → `syncOnStart()`
 *
 * 6 and 7 were missing, and their absence was not a small gap. The only
 * automatic trigger was the offline→online transition, so an inspector with
 * signal all day never uploaded anything, and a phone that queued work
 * offline and was then evicted from memory by the OS — routine on iOS —
 * reopened in coverage and synced nothing in either direction. The header
 * said "Online · N queued" the whole time.
 *
 * Still deliberately no timer: every trigger here is an event.
 */
import { drain, setAutoDrain } from './queue';
import { pullAll } from './pull';
import { platform } from '../platform';

/** How long backgrounded before a foreground counts as "came back". */
const FOREGROUND_THRESHOLD_MS = 60_000;

let hiddenAt: number | null = null;

/**
 * How a finished pull tells the UI to re-read.
 *
 * A pull rewrites sessions, captures, templates and field values in SQLite,
 * but every screen reads through TanStack Query, so without an invalidation
 * they keep rendering the cache they had before it. Only the manual "Sync
 * now" button did this — its own `onSuccess` calls `invalidateQueries()` —
 * which meant an ambient sync fetched everything correctly and changed
 * nothing on screen. A reviewer's decision would land in the database and the
 * inspector would keep seeing "submitted" until they happened to reload.
 *
 * Registered by `main.tsx` rather than imported, because this module must not
 * depend on React or on the app's query client.
 */
let invalidate: (() => void) | null = null;

export function setQueryInvalidator(fn: (() => void) | null): void {
  invalidate = fn;
}

/**
 * Push what we have, then pull what changed. Ordering matters: draining first
 * means the pull's queue guard has fewer rows to skip, so the server's answer
 * lands in the same online window instead of waiting for the next trigger.
 */
export async function syncNow(): Promise<void> {
  try {
    await drain();
  } catch (err) {
    // A failed drain shouldn't cancel the pull — inbound changes are still
    // worth having, and per-task failures are already recorded on the rows.
    console.warn('[sync] drain failed', err);
  }
  await pullAll();
  // The pull is only half the job; the screens have to know it happened.
  invalidate?.();
}

/** Pull-to-refresh and the Sync screen's Refresh button. */
export function refreshNow(): Promise<void> {
  return syncNow();
}

/**
 * Sync once at app start.
 *
 * Separate from `startSyncTriggers` because it fires rather than subscribes,
 * and because a caller may want the listeners without the immediate run.
 * Both directions: a cold start has queued work to push *and* a stale local
 * database to refresh, and a brand-new device has nothing at all until the
 * first pull lands.
 */
export function syncOnStart(): void {
  if (!platform().net.isOnline()) return;
  void syncNow().catch((err) => {
    // Boot must not fail because the network did.
    console.warn('[sync] start-up sync failed', err);
  });
}

/**
 * Wire up the ambient triggers. Returns a teardown function.
 */
export function startSyncTriggers(): () => void {
  // Enqueuing work now schedules a push. Enabled here rather than by default
  // so unit tests driving `enqueue`/`drain` directly stay deterministic.
  setAutoDrain(true);
  const unsubscribeLifecycle = platform().lifecycle.subscribe((state) => {
    if (state === 'background') {
      hiddenAt = Date.now();
      return;
    }
    const away = hiddenAt == null ? 0 : Date.now() - hiddenAt;
    hiddenAt = null;
    if (away > FOREGROUND_THRESHOLD_MS) void syncNow();
  });

  const unsubscribeNetwork = platform().net.subscribe((online) => {
    if (online) void syncNow();
  });

  return () => {
    setAutoDrain(false);
    unsubscribeLifecycle();
    unsubscribeNetwork();
  };
}
